import { env } from '@/lib/env';
import axios from 'axios';

/**
 * Base URL for report downloads — routes through incidents API (/backend/api/reports/*).
 * Master agent is internal-only; reports are proxied via incidents API.
 *
 * Values are read from window.__ENV__ (injected at deploy time via config.js)
 * with fallback to process.env for local development.
 */
export function getMasterAgentApiBase() {
  const backend = (env('REACT_APP_BACKEND_URL') || '').replace(/\/$/, '');
  const orchestrator = (env('REACT_APP_ORCHESTRATOR_URL') || '').trim().replace(/\/$/, '');

  // Production / local nginx gateway — reports via /backend/api/reports/*
  if (backend.endsWith('/backend')) {
    return `${backend}/api`;
  }

  // Local Vibe stack: reports are served by master-agent GET /reports/{file} (:8080).
  if (
    orchestrator &&
    (orchestrator.includes('localhost') || orchestrator.includes('127.0.0.1'))
  ) {
    const backendIsIncidents = backend.includes(':8001');
    if (!backendIsIncidents) {
      return orchestrator;
    }
  }

  // Full local stack — incidents API serves /api/reports/*
  if (backend.includes('localhost') || backend.includes('127.0.0.1')) {
    if (backend.includes(':8001')) {
      return backend.replace(/\/$/, '');
    }
    return 'http://localhost:8001/api';
  }

  if (orchestrator) return orchestrator;

  return '';
}

export function isInlineHtml(value) {
  if (!value || typeof value !== 'string') return false;
  const t = value.trim().toLowerCase();
  return t.startsWith('<!doctype') || t.startsWith('<html');
}

/** Rewrite SPA /reports/*.html links to master-agent GET /reports/{filename} via API GW. */
export function resolveReportFetchUrl(reportUrl) {
  if (!reportUrl || isInlineHtml(reportUrl)) return null;

  const filenameMatch = String(reportUrl).match(/\/reports\/([^/?#]+\.html)(?:[?#].*)?$/i);
  if (!filenameMatch) return reportUrl;

  const apiBase = getMasterAgentApiBase();
  if (!apiBase) return reportUrl;

  return `${apiBase}/reports/${encodeURIComponent(filenameMatch[1])}`;
}

/** Fetch report HTML through the authenticated incidents API (iframe src cannot send JWT). */
export async function fetchReportHtmlContent(reportUrl) {
  if (!reportUrl || isInlineHtml(reportUrl)) {
    return { html: String(reportUrl || '').trim(), fetchUrl: null };
  }

  const fetchUrl = resolveReportFetchUrl(reportUrl) || reportUrl;
  if (!getMasterAgentApiBase() && String(reportUrl).includes('/reports/')) {
    throw new Error(
      'Report API base is not configured. Set REACT_APP_BACKEND_URL so reports load via /backend/api/reports/…'
    );
  }

  const response = await axios.get(fetchUrl, {
    responseType: 'text',
    headers: {
      Accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
    },
    timeout: 30000,
    validateStatus: (status) => status < 500,
  });

  if (response.status !== 200) {
    throw new Error(`Report fetch failed (HTTP ${response.status})`);
  }
  if (isLikelySpaShellHtml(response.data)) {
    throw new Error(
      'Received the Halo app shell instead of report HTML. The report link may point at CloudFront instead of /backend/api/reports/….'
    );
  }

  return { html: response.data, fetchUrl };
}

export function openReportHtmlInNewTab(html, filename = 'report.html') {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const tab = window.open(url, '_blank', 'noopener,noreferrer');
  if (!tab) {
    URL.revokeObjectURL(url);
    throw new Error('Pop-up blocked. Allow pop-ups to open the report.');
  }
  tab.addEventListener?.('load', () => URL.revokeObjectURL(url));
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return url;
}

/** CloudFront SPA fallback often returns index.html instead of the report file. */
export function isLikelySpaShellHtml(html) {
  if (!html || typeof html !== 'string') return false;
  const lower = html.toLowerCase();
  const hasRoot = lower.includes('id="root"') || lower.includes("id='root'");
  const hasBundle = lower.includes('/static/js/') || lower.includes('react');
  const looksLikeReport =
    lower.includes('sap_odata_report') ||
    lower.includes('sales order') ||
    (lower.includes('<table') && lower.includes('</table>'));
  return hasRoot && hasBundle && !looksLikeReport;
}